// Данните на „По категории": целият период от `ordered-items`, групиран по
// категория, плюс предходният равен отрязък за Δ чиповете.
//
// Две тежки заявки (по една на период), затова втората се пуска само при
// включено сравнение. И двете минават през `fetchAllOrderedItems` — таблицата
// е клиентска и не може да групира страница по страница.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";

import type { OrderedItemsFilters } from "../../../../api/reports";
import {
  fetchAllOrderedItems,
  type AllOrderedItems,
} from "../items/orderedItemsFetch";
import { previousPeriodOf } from "../reportPeriod";
import { buildCategoryRows, totalsOf } from "./categoriesMath";
import type { OrderedItem } from "../../../../api/reports";
import type { LunchScope } from "../lunchScope";
import type { CategoriesTotals, CategoryRow } from "./categoriesMath";
import {
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../../lib/businessDay";

/** Отговорът при липсващ период — `enabled` така или иначе не пуска заявката. */
const EMPTY: AllOrderedItems = {
  items: [],
  uniqueItemsCount: 0,
  totalQuantity: 0,
  grandTotal: 0,
};

type Options = {
  dateRange: [Dayjs, Dayjs] | null;
  compare: boolean;
  langCode: string;
  /** „Всички / Без обедно / Само обедно" — пресява изтеглените редове тук. */
  lunchScope: LunchScope;
};

/** Редовете по превключвателя за обедното меню (добавките са „основно меню"). */
const inLunchScope = (items: OrderedItem[], scope: LunchScope) =>
  scope === "all"
    ? items
    : items.filter((item) => (item.is_lunch === true) === (scope === "lunch"));

const sumOf = (items: OrderedItem[]) =>
  items.reduce(
    (sum, item) => ({
      quantity: sum.quantity + Number(item.count),
      revenue: sum.revenue + Number(item.total_sum),
    }),
    { quantity: 0, revenue: 0 },
  );

type Result = {
  rows: CategoryRow[];
  totals: CategoriesTotals;
  /** `null` = сравнението е изключено или предходният период още се зарежда. */
  previousTotals: { quantity: number; revenue: number } | null;
  /** Има ли в периода продажби от обедно меню (за превключвателя). */
  hasLunch: boolean;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
};

export const useCategoryRows = ({
  dateRange,
  compare,
  langCode,
  lunchScope,
}: Options): Result => {
  const currentFilters = useMemo<OrderedItemsFilters | null>(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return null;
    return {
      start_date: businessDayStartFrom(dateRange[0]).format("YYYY-MM-DD HH:mm:ss"),
      end_date: businessDayEndFrom(dateRange[1]).format("YYYY-MM-DD HH:mm:ss"),
      language_code: langCode,
    };
  }, [dateRange, langCode]);

  // Краят на предходния период се подрязва до изтеклото време, когато
  // избраният още тече — иначе непълен ден стои срещу пълен (виж reportPeriod).
  const previousFilters = useMemo<OrderedItemsFilters | null>(() => {
    if (!compare || !dateRange?.[0] || !dateRange?.[1]) return null;
    // Предходният период се строи от СЪЩИТЕ граници, с които се прави
    // заявката (`startOf`/`endOf` на деня), а не от суровите от календара.
    // Изборът без час връща край 00:00 и неравни прозорци: „01–03.09" се
    // сравняваше с два дни вместо три и Δ показваше ръст, какъвто няма.
    const previous = previousPeriodOf(
      businessDayStartFrom(dateRange[0]),
      businessDayEndFrom(dateRange[1]),
    );
    return {
      start_date: previous.start.format("YYYY-MM-DD HH:mm:ss"),
      end_date: previous.end.format("YYYY-MM-DD HH:mm:ss"),
      language_code: langCode,
    };
  }, [compare, dateRange, langCode]);

  const current = useQuery({
    queryKey: ["reports", "ordered-items", "all", currentFilters],
    queryFn: () =>
      currentFilters ? fetchAllOrderedItems(currentFilters) : Promise.resolve(EMPTY),
    enabled: currentFilters !== null,
    staleTime: 0,
  });

  const previous = useQuery({
    queryKey: ["reports", "ordered-items", "all", previousFilters],
    queryFn: () =>
      previousFilters
        ? fetchAllOrderedItems(previousFilters)
        : Promise.resolve(EMPTY),
    enabled: previousFilters !== null,
    staleTime: 0,
  });

  const currentItems = useMemo(
    () => inLunchScope(current.data?.items ?? [], lunchScope),
    [current.data, lunchScope],
  );
  const previousItems = useMemo(
    () =>
      previousFilters && previous.data
        ? inLunchScope(previous.data.items, lunchScope)
        : null,
    [previousFilters, previous.data, lunchScope],
  );

  const rows = useMemo(
    () => buildCategoryRows(currentItems, previousItems),
    [currentItems, previousItems],
  );

  const totals = useMemo(() => {
    const grouped = totalsOf(rows);
    // Броят категории идва от кофите, а сумите — от агрегатите на BE-то:
    // те са смятани върху целия набор и не зависят от закръгления в браузъра.
    // Пресятият по обедното набор няма BE агрегат — сумира се тук.
    if (lunchScope !== "all") return grouped;
    return {
      categories: grouped.categories,
      quantity: current.data?.totalQuantity ?? grouped.quantity,
      revenue: current.data?.grandTotal ?? grouped.revenue,
    };
  }, [rows, current.data, lunchScope]);

  const previousTotals = useMemo(() => {
    if (!previousFilters || !previous.data || !previousItems) return null;
    if (lunchScope !== "all") return sumOf(previousItems);
    return {
      quantity: previous.data.totalQuantity,
      revenue: previous.data.grandTotal,
    };
  }, [previousFilters, previous.data, previousItems, lunchScope]);

  const hasLunch = useMemo(
    () => (current.data?.items ?? []).some((item) => item.is_lunch === true),
    [current.data],
  );

  return {
    rows,
    totals,
    previousTotals,
    hasLunch,
    isLoading: current.isLoading,
    isFetching: current.isFetching || previous.isFetching,
    refetch: () => {
      current.refetch();
      // Изключеното сравнение няма какво да презарежда — `refetch()` върху
      // изключена заявка все пак стреля към сървъра.
      if (previousFilters) previous.refetch();
    },
  };
};
